import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Webhook, Zap, Sheet, MessageSquare, Mail, Building2, Cloud, Briefcase,
  Loader2, CheckCircle2, ExternalLink, Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface IntegrationRow {
  id: string;
  provider: string;
  config: Record<string, any>;
  is_enabled: boolean;
}

interface CatalogItem {
  id: string;
  name: string;
  description: string;
  icon: any;
  status: 'available' | 'coming-soon' | 'external';
  category: 'trigger' | 'destination' | 'crm';
  action?: () => void;
  needsConfig?: boolean;
}

const Integrations = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [integrations, setIntegrations] = useState<IntegrationRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const [zapierUrl, setZapierUrl] = useState('');
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistFor, setWaitlistFor] = useState('');

  useEffect(() => { if (user) fetchIntegrations(); }, [user]);

  const fetchIntegrations = async () => {
    if (!user) return;
    setIsLoading(true);
    const { data, error } = await supabase.from('integrations' as any).select('*').eq('user_id', user.id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setIntegrations((data as unknown as IntegrationRow[]) || []);
    }
    setIsLoading(false);
  };

  const getIntegration = (provider: string) => integrations.find(i => i.provider === provider);

  const saveZapier = async () => {
    if (!user) return;
    if (!zapierUrl.startsWith('https://hooks.zapier.com/')) {
      return toast({ title: 'Invalid webhook URL', description: 'Must start with https://hooks.zapier.com/', variant: 'destructive' });
    }
    const existing = getIntegration('zapier');
    const payload = {
      user_id: user.id,
      provider: 'zapier',
      config: { webhook_url: zapierUrl },
      is_enabled: true,
    };
    const { error } = existing
      ? await supabase.from('integrations' as any).update({ config: payload.config, is_enabled: true }).eq('id', existing.id)
      : await supabase.from('integrations' as any).insert(payload);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Zapier connected', description: 'Webhook URL saved.' });
    setConfigOpen(false);
    setZapierUrl('');
    fetchIntegrations();
  };

  const toggleIntegration = async (provider: string, enabled: boolean) => {
    const row = getIntegration(provider);
    if (!row) return;
    const { error } = await supabase.from('integrations' as any).update({ is_enabled: enabled }).eq('id', row.id);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    fetchIntegrations();
  };

  const disconnect = async (provider: string) => {
    const row = getIntegration(provider);
    if (!row) return;
    const { error } = await supabase.from('integrations' as any).delete().eq('id', row.id);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Disconnected' });
    fetchIntegrations();
  };

  const joinWaitlist = async (provider: string) => {
    if (!user) return;
    const existing = getIntegration(`waitlist_${provider}`);
    if (existing) {
      toast({ title: "You're already on the waitlist for " + provider });
      setWaitlistOpen(false);
      return;
    }
    const { error } = await supabase.from('integrations' as any).insert({
      user_id: user.id,
      provider: `waitlist_${provider}`,
      config: { requested_at: new Date().toISOString() },
      is_enabled: false,
    });
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Added to waitlist', description: `We'll email you when ${provider} launches.` });
    setWaitlistOpen(false);
    fetchIntegrations();
  };

  const openWaitlist = (name: string) => {
    setWaitlistFor(name);
    setWaitlistOpen(true);
  };

  const catalog: CatalogItem[] = [
    {
      id: 'webhooks',
      name: 'Webhooks',
      description: 'Send form submissions to any HTTPS endpoint with retries and HMAC signature.',
      icon: Webhook,
      status: 'available',
      category: 'trigger',
      action: () => navigate('/api'),
    },
    {
      id: 'zapier',
      name: 'Zapier',
      description: 'Trigger thousands of apps when a form is submitted. Paste your Zap webhook URL to connect.',
      icon: Zap,
      status: 'available',
      category: 'trigger',
      needsConfig: true,
    },
    {
      id: 'google_sheets',
      name: 'Google Sheets',
      description: 'Append every new submission as a row in a Google Sheet.',
      icon: Sheet,
      status: 'coming-soon',
      category: 'destination',
    },
    {
      id: 'slack',
      name: 'Slack',
      description: 'Post new submissions and lead alerts to a Slack channel.',
      icon: MessageSquare,
      status: 'coming-soon',
      category: 'destination',
    },
    {
      id: 'mailchimp',
      name: 'Mailchimp',
      description: 'Sync contacts into a Mailchimp audience automatically.',
      icon: Mail,
      status: 'coming-soon',
      category: 'destination',
    },
    {
      id: 'hubspot',
      name: 'HubSpot',
      description: 'Create HubSpot contacts and deals from every form submission.',
      icon: Building2,
      status: 'coming-soon',
      category: 'crm',
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      description: 'Push leads and cases into Salesforce with field mapping.',
      icon: Cloud,
      status: 'coming-soon',
      category: 'crm',
    },
    {
      id: 'pipedrive',
      name: 'Pipedrive',
      description: 'Create Pipedrive persons and deals from submissions.',
      icon: Briefcase,
      status: 'coming-soon',
      category: 'crm',
    },
  ];

  const openConfig = (item: CatalogItem) => {
    setActiveProvider(item.id);
    if (item.id === 'zapier') {
      setZapierUrl(getIntegration('zapier')?.config?.webhook_url || '');
      setConfigOpen(true);
    } else if (item.action) {
      item.action();
    }
  };

  return (
    <DashboardLayout>
      <Helmet><title>Integrations | Bravax Forms</title></Helmet>
      <div className="p-4 sm:p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Integrations</h1>
          <p className="text-sm text-muted-foreground">Connect Bravax Forms with the rest of your stack.</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            {['trigger', 'destination', 'crm'].map(cat => (
              <div key={cat} className="space-y-3">
                <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {cat === 'trigger' && 'Triggers & Automation'}
                  {cat === 'destination' && 'Destinations'}
                  {cat === 'crm' && 'CRM & Sales'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {catalog.filter(i => i.category === cat).map(item => {
                    const connected = !!getIntegration(item.id);
                    const onWaitlist = !!getIntegration(`waitlist_${item.id}`);
                    return (
                      <Card key={item.id} className="border border-border/50 hover:border-primary/30 transition-colors">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <item.icon className="h-5 w-5 text-primary" />
                            </div>
                            {connected && item.status === 'available' && (
                              <Badge variant="secondary" className="gap-1 bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                <CheckCircle2 className="h-3 w-3" /> Connected
                              </Badge>
                            )}
                            {item.status === 'coming-soon' && !onWaitlist && (
                              <Badge variant="outline" className="text-xs">Coming soon</Badge>
                            )}
                            {onWaitlist && (
                              <Badge variant="secondary" className="text-xs">On waitlist</Badge>
                            )}
                          </div>
                          <CardTitle className="text-base mt-3">{item.name}</CardTitle>
                          <CardDescription className="text-xs leading-relaxed">{item.description}</CardDescription>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {item.status === 'available' ? (
                            connected ? (
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2 flex-1">
                                  <Switch
                                    checked={getIntegration(item.id)?.is_enabled ?? true}
                                    onCheckedChange={(v) => toggleIntegration(item.id, v)}
                                  />
                                  <Label className="text-xs">Enabled</Label>
                                </div>
                                {item.needsConfig && (
                                  <Button variant="outline" size="sm" onClick={() => openConfig(item)}>Configure</Button>
                                )}
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => disconnect(item.id)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" className="w-full" onClick={() => openConfig(item)}>
                                {item.needsConfig ? 'Connect' : 'Open'} <ExternalLink className="ml-1 h-3 w-3" />
                              </Button>
                            )
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full"
                              disabled={onWaitlist}
                              onClick={() => openWaitlist(item.name)}
                            >
                              {onWaitlist ? 'On waitlist' : 'Join waitlist'}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Zapier config dialog */}
        <Dialog open={configOpen} onOpenChange={setConfigOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Connect Zapier</DialogTitle>
              <DialogDescription>
                In Zapier, create a Zap with a <strong>Webhooks by Zapier</strong> trigger (Catch Hook). Paste the URL Zapier gives you below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Zap Webhook URL</Label>
              <Input value={zapierUrl} onChange={(e) => setZapierUrl(e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/..." />
              <p className="text-xs text-muted-foreground">
                We'll POST a JSON payload of each new submission. Check the Zap's run history to confirm.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancel</Button>
              <Button onClick={saveZapier}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Waitlist dialog */}
        <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Join {waitlistFor} waitlist</DialogTitle>
              <DialogDescription>
                We prioritize integrations by demand. You'll get an email when {waitlistFor} is ready.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWaitlistOpen(false)}>Cancel</Button>
              <Button onClick={() => joinWaitlist(waitlistFor.toLowerCase().replace(/\s+/g, '_'))}>Add me</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Integrations;
